import { InputOTP6 } from "@/components/molecules/otp";
import { Button } from "@/components/ui/button";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useTranslation } from "@/features/utils/i18n";
import { useEffect, useRef, useState } from "react";

const RETRY_DELAY_SECONDS = 60;

/**
 * The only verified factor of the signup: the 6-digit code mailed to the
 * address the user just typed.
 */
export const VerifyEmail = (props: {
  email: string;
  onVerify: (otp: string) => void;
  onResend: () => Promise<void>;
  onBack: () => void;
  isLoading?: boolean;
}) => {
  const { t } = useTranslation();
  const [retryTimeLeft, setRetryTimeLeft] = useState(RETRY_DELAY_SECONDS);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startRetryTimer = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setRetryTimeLeft(RETRY_DELAY_SECONDS);
    intervalRef.current = setInterval(() => {
      setRetryTimeLeft((prev) => {
        if (prev <= 1) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // The code was sent right before this step was shown: start counting now.
  useEffect(() => {
    startRetryTimer();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  const handleResend = async () => {
    await props.onResend();
    startRetryTimer();
  };

  return (
    <form onSubmit={(e) => e.preventDefault()}>
      <CardHeader>
        <CardDescription>
          {t("email.codeSent", { email: props.email })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid gap-2 flex items-center justify-center">
          <Label>{t("otp.label")}</Label>
          <InputOTP6
            required
            autoFocus
            onChange={(value) => {
              if (value.length === 6) {
                props.onVerify(value);
              }
            }}
            disabled={props.isLoading}
          />
        </div>
      </CardContent>
      <CardFooter className="flex flex-col gap-2">
        <Button
          type="button"
          variant="link"
          className="p-0"
          onClick={handleResend}
          disabled={retryTimeLeft > 0 || props.isLoading}
        >
          {retryTimeLeft > 0
            ? t("actions.retryIn", { seconds: retryTimeLeft })
            : t("actions.resendCode")}
        </Button>
        <Button
          type="button"
          variant="link"
          className="p-0"
          onClick={props.onBack}
          disabled={props.isLoading}
        >
          {t("actions.back")}
        </Button>
      </CardFooter>
    </form>
  );
};
